import type { SyntheticEvent } from 'react';
import Highlighter from 'react-highlight-words';
import type { SuggestedShareList } from '@frameio/graphql-client/generated';
import { imageLoader } from '@frameio/utils';
import { Avatar, AvatarGroup, Text, theme } from '@frameio/vapor';
import { IconArrowUpRight } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ShareListAccessRow';
import { RowContainerButton } from './MemberAccessRow.styles';

TEXT.DEFINE({
  /** Subtitle shown when the current user created the share list */
  createdByYou: 'by you',
  /** Subtitle shown when another user created the share list */
  createdByOther: (creatorName: string) => `by ${creatorName}`,
  /** Accessible label for a share list entry */
  label: (name: string) => `Open share list ${name}`,
});

export type ShareListAccessRowProps = {
  avatarIcons: SuggestedShareList['avatarIcons'];
  creatorName?: string;
  highlight?: boolean;
  isCreatedByCurrentUser: boolean;
  memberCount?: number;
  name: string;
  onClick?: (evt: SyntheticEvent<HTMLButtonElement>) => void;
  shareListId?: string;
  textInput?: string;
};

export function ShareListAccessRow({
  avatarIcons,
  creatorName,
  highlight,
  isCreatedByCurrentUser,
  memberCount,
  name,
  onClick,
  shareListId,
  textInput,
}: ShareListAccessRowProps) {
  const searchWords = textInput?.split(/\s+/).filter(Boolean) ?? [];
  const [first, second] = avatarIcons ?? [];
  const firstSrc = first?.profileImageUrl
    ? imageLoader({ src: first.profileImageUrl, width: 48 })
    : undefined;
  const secondSrc = second?.profileImageUrl
    ? imageLoader({ src: second.profileImageUrl, width: 48 })
    : undefined;

  const subtitle = isCreatedByCurrentUser
    ? (TEXT.createdByYou as string)
    : (TEXT.createdByOther(creatorName ?? '') as string);

  return (
    <RowContainerButton
      $isHighlighted={highlight}
      name={shareListId}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      style={{
        position: 'relative',
        /**
         * Reserve space on the right so the absolutely-positioned IconArrowUpRight
         * doesn't overlap row content.
         */
        paddingRight: theme.spaces[24],
      }}
    >
      <AvatarGroup
        interactive={false}
        overlap={12}
        style={{ gridArea: 'avatar' }}
        variant="angled"
      >
        <Avatar alt={first?.name ?? ''} size={20} src={firstSrc} />
        <Avatar alt={second?.name ?? ''} size={20} src={secondSrc} />
      </AvatarGroup>
      <Text
        color="body"
        style={{ gridArea: 'name', textAlign: 'left' }}
        variant="body"
      >
        <Highlighter
          autoEscape={true}
          highlightStyle={{
            backgroundColor: theme.colors.background.interactive.highlight,
            color: theme.colors.text.body,
          }}
          searchWords={searchWords}
          textToHighlight={name}
        />
        {typeof memberCount === 'number' && (
          <>
            {' '}
            <Text
              as="span"
              color="tertiary"
              display="inline-block"
              variant="body"
            >
              {` (${memberCount})`}
            </Text>
          </>
        )}
      </Text>
      <Text style={{ gridArea: 'email' }}>{subtitle}</Text>
      <IconArrowUpRight
        color={theme.colors.icon.secondary}
        size={16}
        style={{ position: 'absolute', right: 8 }}
      />
    </RowContainerButton>
  );
}
